import React, { useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';

// Actions
import * as LineActions from 'store/ducks/lines/actions';
import * as ReportActions from 'store/ducks/reports/actions';

// Types
import { LineInterface } from 'store/ducks/lines/types';
import { ApplicationState } from 'store';

// Components
import { SearchAsyncComponent } from 'components/SearchAsyncComponent';

// Helpers
import { useDebouncedSearch } from 'helpers/debounceHook';
import { promiseDispatch } from 'store/utils/promiseAction';
import { setCurrentSearchTerm } from 'helpers/tableFilters';

// Styles
import useStyles from './lineFilter.styles';

interface LineFilterProps {
  singleLineFilter?: boolean;
}

type Props = LineFilterProps;

const storegeLimit =
  JSON.parse(window.localStorage.getItem('persist:root') as string)
    ?.localLimit || '10';
const { localLimit } = JSON.parse(storegeLimit) || 10;

async function searchLine(filter: string) {
  let searchObj = null;
  if (/^\d+$/.test(filter)) {
    searchObj = {
      prefix: `%${filter}%`,
      _includes: 'sections,linestop',
      _limit: localLimit,
    };
  } else {
    searchObj = {
      name: `%${filter}%`,
      _includes: 'sections,linestop',
      _limit: localLimit,
    };
  }
  return promiseDispatch(LineActions.filterLinesRequest(searchObj));
}

const useSearchLine = () => useDebouncedSearch(searchLine);

const LineFilter: React.FC<Props> = ({ singleLineFilter }) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [
    autoCompleteText,
    setAutoCompleteText,
    searchResults,
  ] = useSearchLine();

  const filteredLines = useSelector(
    (state: ApplicationState) => state.reports.selectedLines,
  );

  const linesList = useSelector(
    (state: ApplicationState) => state.lines.linesList || [],
  );

  useEffect(() => {
    dispatch(
      LineActions.listLinesRequest({
        body: { _includes: 'sections,linestop' },
      }),
    );
  }, [dispatch]);

  const handleLine = (line: LineInterface, isRadio?: boolean) => {
    if (isRadio) {
      dispatch(
        ReportActions.setSelectedLines({
          selectedLines: [{ ...line }],
        }),
      );

      return;
    }

    const updated = [...filteredLines];
    const index = filteredLines.findIndex((e) => e.id === line.id);

    if (index === -1) updated.push({ ...line });
    else updated.splice(index, 1);

    dispatch(
      ReportActions.setSelectedLines({
        selectedLines: updated,
      }),
    );
  };

  return (
    <Box>
      <SearchAsyncComponent
        placeholder='Digite o nome da linha'
        loading={searchResults.loading}
        classes={{
          root: classes.root,
        }}
        inputSize='small'
        onChange={(text: string) => {
          setCurrentSearchTerm(text);
          setAutoCompleteText(text);
          if (!text) {
            dispatch(
              LineActions.listLinesRequest({
                params: { _includes: 'sections,linestop' },
                filtering: true,
              }),
            );
          }
        }}
        value={autoCompleteText}
      />

      <Box marginTop={1}>
        <List dense>
          {Array.isArray(linesList) &&
            linesList.map((item: LineInterface) => (
              <ListItem key={item.id} button>
                <ListItemText
                  id={item.id}
                  primary={item.name}
                  onClick={() => {
                    if (singleLineFilter) {
                      handleLine(item, true);
                      return;
                    }
                    handleLine(item);
                  }}
                  classes={{ root: classes.itemText }}
                />
                <ListItemSecondaryAction>
                  {singleLineFilter ? (
                    <Radio
                      edge='end'
                      color='primary'
                      onChange={() => handleLine(item, true)}
                      checked={!!filteredLines.find((e) => e.id === item.id)}
                      inputProps={{ 'aria-labelledby': item.id }}
                    />
                  ) : (
                    <Checkbox
                      edge='end'
                      color='primary'
                      onChange={() => handleLine(item)}
                      checked={!!filteredLines.find((e) => e.id === item.id)}
                      inputProps={{ 'aria-labelledby': item.id }}
                    />
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  );
};

export { LineFilter };
export default LineFilter;
